import type { Question } from './topics';
import { options, pick } from './util';

// ─────────────────────────────────────────────────────────────────────────────
// Italian, by rule. Present tense with the two spelling rules applied:
//   • -care / -gare keep the hard sound: tu cerchi, noi paghiamo
//   • -iare don't double the i: tu studi, noi mangiamo
// -ire verbs here are the non-isc kind (dormo, not capisco).
// ─────────────────────────────────────────────────────────────────────────────

const PEOPLE = [['io'], ['tu'], ['lui', 'lei'], ['noi'], ['voi'], ['loro']];
const ENDINGS: Record<'are' | 'ere' | 'ire', string[]> = {
  are: ['o', 'i', 'a', 'iamo', 'ate', 'ano'],
  ere: ['o', 'i', 'e', 'iamo', 'ete', 'ono'],
  ire: ['o', 'i', 'e', 'iamo', 'ite', 'ono'],
};

export const IT_VERBS: Record<string, string> = {
  parlare: 'to speak', abitare: 'to live', lavorare: 'to work', ascoltare: 'to listen', guardare: 'to watch',
  comprare: 'to buy', cantare: 'to sing', ballare: 'to dance', mangiare: 'to eat', cercare: 'to look for',
  pagare: 'to pay', studiare: 'to study', cominciare: 'to start',
  prendere: 'to take', leggere: 'to read', scrivere: 'to write', vedere: 'to see', vendere: 'to sell',
  credere: 'to believe', chiedere: 'to ask', correre: 'to run', mettere: 'to put', ricevere: 'to receive',
  dormire: 'to sleep', partire: 'to leave', aprire: 'to open', sentire: 'to hear / feel', offrire: 'to offer',
};

export function itPresent(verb: string, i: number): string {
  const type = verb.slice(-3) as 'are' | 'ere' | 'ire';
  let stem = verb.slice(0, -3);
  let end = ENDINGS[type][i];
  if (type === 'are' && end.startsWith('i')) {
    if (/[cg]$/.test(stem)) stem += 'h';           // cerchi, paghiamo
    else if (stem.endsWith('i')) end = end.slice(1); // studi + '' = studi; studi + amo = studiamo
  }
  return stem + end;
}

export function itRegular(n: number): Question[] {
  const verbs = Object.keys(IT_VERBS);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const verb = pick(verbs);
    const i = Math.floor(Math.random() * 6);
    const answer = itPresent(verb, i);
    const typed = Math.random() < 0.5;
    qs.push({
      key: `it_present:${verb}:${i}`, topic: 'it_present', kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Present tense · write the verb' : 'Present tense · choose the verb',
      prompt: `${pick(PEOPLE[i])} ___ (${verb})`, hint: IT_VERBS[verb], answers: [answer],
      options: options(answer, [0, 1, 2, 3, 4, 5].map(j => itPresent(verb, j))),
    });
  }
  return qs;
}

const ESSERE = ['sono', 'sei', 'è', 'siamo', 'siete', 'sono'];
const AVERE = ['ho', 'hai', 'ha', 'abbiamo', 'avete', 'hanno'];

export function itEssereAvere(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const essere = Math.random() < 0.5;
    const table = essere ? ESSERE : AVERE;
    const i = Math.floor(Math.random() * 6);
    const typed = Math.random() < 0.4;
    qs.push({
      key: `it_essere_avere:${essere}:${i}`, topic: 'it_essere_avere', kind: typed ? 'type' : 'choice',
      instruction: `${essere ? 'Essere' : 'Avere'} · present tense`,
      prompt: `${pick(PEOPLE[i])} ___ (${essere ? 'essere' : 'avere'})`, hint: essere ? 'to be' : 'to have',
      answers: [table[i]], options: options(table[i], table),
    });
  }
  return qs;
}

type Noun = { w: string; g: 'm' | 'f'; en: string };
const IT_NOUNS: Noun[] = [
  { w: 'libro', g: 'm', en: 'book' }, { w: 'ragazzo', g: 'm', en: 'boy' }, { w: 'giorno', g: 'm', en: 'day' },
  { w: 'cane', g: 'm', en: 'dog' }, { w: 'gatto', g: 'm', en: 'cat' }, { w: 'caffè', g: 'm', en: 'coffee' },
  { w: 'problema', g: 'm', en: 'problem' }, { w: 'amico', g: 'm', en: 'friend' }, { w: 'orologio', g: 'm', en: 'watch' },
  { w: 'uomo', g: 'm', en: 'man' }, { w: 'aereo', g: 'm', en: 'airplane' }, { w: 'studente', g: 'm', en: 'student' },
  { w: 'zaino', g: 'm', en: 'backpack' }, { w: 'zio', g: 'm', en: 'uncle' }, { w: 'specchio', g: 'm', en: 'mirror' },
  { w: 'sport', g: 'm', en: 'sport' }, { w: 'stadio', g: 'm', en: 'stadium' }, { w: 'gnomo', g: 'm', en: 'gnome' },
  { w: 'psicologo', g: 'm', en: 'psychologist' }, { w: 'yogurt', g: 'm', en: 'yogurt' },
  { w: 'casa', g: 'f', en: 'house' }, { w: 'ragazza', g: 'f', en: 'girl' }, { w: 'scuola', g: 'f', en: 'school' },
  { w: 'notte', g: 'f', en: 'night' }, { w: 'macchina', g: 'f', en: 'car' }, { w: 'pizza', g: 'f', en: 'pizza' },
  { w: 'città', g: 'f', en: 'city' }, { w: 'mano', g: 'f', en: 'hand' }, { w: 'foto', g: 'f', en: 'photo' },
  { w: 'acqua', g: 'f', en: 'water' }, { w: 'isola', g: 'f', en: 'island' }, { w: 'ora', g: 'f', en: 'hour' },
  { w: 'università', g: 'f', en: 'university' }, { w: 'amica', g: 'f', en: 'friend (f)' },
];

/** il · lo (s+consonant, z, gn, ps, x, y) · l' (vowel) — la · l' (vowel). */
export function itArticle(x: Noun): { a: string; why: string } {
  const w = x.w.toLowerCase();
  const vowel = /^[aeiouàèéìòù]/.test(w);
  if (x.g === 'f') return vowel ? { a: "l'", why: 'Feminine before a vowel → l\'' } : { a: 'la', why: 'Feminine → la' };
  if (vowel) return { a: "l'", why: 'Masculine before a vowel → l\'' };
  if (/^(s[^aeiou]|z|gn|ps|pn|x|y)/.test(w)) return { a: 'lo', why: 'Masculine before s + consonant, z, gn, ps, x or y → lo' };
  return { a: 'il', why: x.w === 'problema' ? 'problema is masculine, despite the -a → il' : 'Masculine → il' };
}

export function itArticles(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = pick(IT_NOUNS);
    const { a, why } = itArticle(x);
    const prompt = a === "l'" ? `___${x.w}` : `___ ${x.w}`;
    qs.push({
      key: `it_articles:${x.w}`, topic: 'it_articles', kind: 'choice', instruction: 'Il, lo, la or l\'?',
      prompt, hint: x.en, why, options: ['il', 'lo', 'la', "l'"], answers: [a],
    });
  }
  return qs;
}
